import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Divider, IconButton } from "@mui/material";
import { Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperType } from "swiper";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

import { SliderArrowLeftIcon, SliderArrowRightIcon } from "../../../../assets/icons/go-service-icons";

interface PaginationControlsProps {
  totalItems: number;
  activeIndex: number;
  onPrev: () => void;
  onNext: () => void;
  onSlideChange: (index: number) => void;
  isRTL: boolean;
}

interface SliderProps {
  items: React.ReactNode[];
  isLoading?: boolean;
  loadingComponent?: React.ReactNode;
  loadingCount?: number;
}

const PaginationControls = ({
  totalItems,
  activeIndex,
  onPrev,
  onNext,
  onSlideChange,
  isRTL,
}: PaginationControlsProps) => {
  if (totalItems === 0) return null;

  return (
    <Box sx={styles.paginationRoot}>
      <IconButton onClick={onPrev} size="small">
        {!isRTL ? <SliderArrowLeftIcon /> : <SliderArrowRightIcon />}
      </IconButton>

      <Box sx={styles.paginationContainer}>
        {[...Array(totalItems)].map((_, index) => (
          <Box
            key={index}
            onClick={() => onSlideChange(index)}
            sx={{
              ...styles.paginationBullet,
              bgcolor: index === activeIndex ? "var(--Brand-600, #008B07)" : "#E4E7EC",
              width: index === activeIndex ? "12px" : "8px",
              height: index === activeIndex ? "12px" : "8px",
            }}
          />
        ))}
      </Box>

      <IconButton onClick={onNext} size="small">
        {!isRTL ? <SliderArrowRightIcon /> : <SliderArrowLeftIcon />}
      </IconButton>
    </Box>
  );
};

const Slider = ({ items, isLoading, loadingComponent, loadingCount = 5 }: SliderProps) => {
  const { i18n } = useTranslation();
  const isRTL = i18n.language !== "en";

  const [swiperInstance, setSwiperInstance] = useState<SwiperType | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const handlePrev = () => {
    if (swiperInstance) swiperInstance.slidePrev();
  };

  const handleNext = () => {
    if (swiperInstance) swiperInstance.slideNext();
  };

  const handleSlideChange = (index: number) => {
    swiperInstance?.slideTo(index);
  };

  const renderSlides = () => {
    if (isLoading && loadingComponent) {
      return [...Array(loadingCount)].map((_, index) => (
        <SwiperSlide key={index} style={{ width: "auto" }}>
          {loadingComponent}
        </SwiperSlide>
      ));
    }

    return items.map((item, index) => (
      <SwiperSlide key={index} style={{ width: "auto" }}>
        {item}
      </SwiperSlide>
    ));
  };

  return (
    <Box sx={styles.root}>
      <Box sx={styles.container}>
        <Swiper
          modules={[Navigation, Pagination]}
          onSwiper={(swiper: SwiperType) => {
            setSwiperInstance(swiper);
            swiper.on("slideChange", () => setActiveIndex(swiper.activeIndex));
          }}
          slidesPerView="auto"
          spaceBetween={16}
          style={{ width: "100%", position: "relative" }}
        >
          {renderSlides()}
        </Swiper>

        <Divider sx={{ borderColor: "#EAECF0", margin: "0 -24px" }} orientation="horizontal" />

        <PaginationControls
          totalItems={isLoading ? loadingCount : items.length}
          activeIndex={activeIndex}
          onPrev={handlePrev}
          onNext={handleNext}
          onSlideChange={handleSlideChange}
          isRTL={isRTL}
        />
      </Box>
    </Box>
  );
};

const styles = {
  root: {
    width: "100%",
  },
  container: {
    position: "relative" as const,
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  paginationRoot: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "8px",
  },
  paginationContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "8px",
  },
  paginationBullet: {
    borderRadius: "50%",
    cursor: "pointer",
    transition: "background-color 0.3s",
  },
};

export default Slider;
